import { createContext, useContext, useState, useEffect, useRef } from 'react';

const ThemeContext = createContext({
  currentTheme: 'light',
  changeCurrentTheme: () => {},
});

export default function ThemeProvider({ children }) {
  // Determine initial theme: localStorage or system preference
  const getInitialTheme = () => {
    try {
      const stored = localStorage.getItem('theme');
      if (stored) return stored;
      return 'light';
    } catch {
      return 'light';
    }
  };

  const [theme, setTheme] = useState(getInitialTheme);

  // Refs to control one-time init suppression and animation timeouts
  const didInit = useRef(false);
  const animTimeoutRef = useRef(null);

  const changeCurrentTheme = newTheme => {
    const root = document.documentElement;

    const apply = () => {
      // Add short-lived class to animate color properties globally
      root.classList.add('theme-animating');
      setTheme(newTheme);
      localStorage.setItem('theme', newTheme);

      // Clear and set timeout to remove the animating class
      if (animTimeoutRef.current) window.clearTimeout(animTimeoutRef.current);
      animTimeoutRef.current = window.setTimeout(() => {
        root.classList.remove('theme-animating');
      }, 300);
    };

    // Progressive enhancement: View Transitions API when available
    if (document.startViewTransition) {
      // Wrap state update inside the transition callback
      document.startViewTransition(() => apply());
    } else {
      apply();
    }
  };

  useEffect(() => {
    const root = document.documentElement;

    const syncThemeClasses = () => {
      if (theme === 'light') {
        root.classList.remove('dark');
        root.style.colorScheme = 'light';
      } else {
        root.classList.add('dark');
        root.style.colorScheme = 'dark';
      }
    };

    // Prevent transitions on first paint only
    if (!didInit.current) {
      root.classList.add('**:transition-none!');
      syncThemeClasses();
      const id = window.setTimeout(() => {
        root.classList.remove('**:transition-none!');
      }, 1);
      didInit.current = true;
      return () => window.clearTimeout(id);
    }

    // Subsequent updates: just sync the classes (animations handled by .theme-animating)
    syncThemeClasses();
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ currentTheme: theme, changeCurrentTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useThemeProvider = () => useContext(ThemeContext);
